/**
 * File upload hook with explicit state machine.
 * States: idle → uploading → success/error
 */

import { useState, useCallback } from 'react';
import type { 
  FileUploadService, 
  FileUploadResult, 
  FileUploadErrorCodeType
} from '@airclou/utils';

// Explicit state machine for upload UI
export type UploadState =
  | { status: 'idle' }
  | { status: 'uploading'; progress?: number; file: File }
  | { status: 'success'; result: FileUploadResult }
  | { status: 'error'; error: { code: FileUploadErrorCodeType; message: string } };

export interface UseFileUploadOptions {
  /** File upload service instance */
  uploadService: FileUploadService;
  /** Category for uploads (e.g. 'covers', 'profiles') */
  category: string;
  /** Allowed MIME types for client-side validation */
  allowedMimeTypes?: string[];
  /** Max file size in bytes for client-side validation */
  maxFileSize?: number;
  /** Callback on successful upload */
  onSuccess?: (result: FileUploadResult) => void;
  /** Callback on upload error */
  onError?: (error: { code: string; message: string }) => void;
}

export interface UseFileUploadReturn {
  /** Current upload state */
  state: UploadState;
  /** Upload a file */
  upload: (file: File) => Promise<void>;
  /** Reset to idle state */
  reset: () => void;
  /** Computed: is currently uploading */
  isUploading: boolean;
  /** Computed: upload succeeded */
  isSuccess: boolean;
  /** Computed: upload failed */
  isError: boolean;
  /** Last successful upload result (convenience) */
  result: FileUploadResult | null;
  /** Last error (convenience) */
  error: { code: string; message: string } | null;
}

const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export function useFileUpload(options: UseFileUploadOptions): UseFileUploadReturn {
  const { 
    uploadService, 
    category, 
    allowedMimeTypes, 
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    onSuccess,
    onError 
  } = options;

  const [state, setState] = useState<UploadState>({ status: 'idle' });

  const upload = useCallback(async (file: File) => {
    // Client-side validation
    if (allowedMimeTypes && !allowedMimeTypes.includes(file.type)) {
      const error = {
        code: 'INVALID_MIME_TYPE' as FileUploadErrorCodeType,
        message: `File type ${file.type} is not allowed. Allowed: ${allowedMimeTypes.join(', ')}`,
      };
      setState({ status: 'error', error });
      onError?.(error);
      return;
    }

    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      const error = {
        code: 'FILE_TOO_LARGE' as FileUploadErrorCodeType,
        message: `File size exceeds ${maxSizeMB}MB limit`,
      };
      setState({ status: 'error', error });
      onError?.(error);
      return;
    }

    // Start upload
    setState({ status: 'uploading', file });

    try {
      const response = await uploadService.upload(file, { category });

      if (response.success) {
        setState({ status: 'success', result: response.data });
        onSuccess?.(response.data);
      } else {
        // Type narrowing: response.success is false, so error exists
        const errorResponse = response as { success: false; error: { code: FileUploadErrorCodeType; message: string } };
        setState({ status: 'error', error: errorResponse.error });
        onError?.(errorResponse.error);
      }
    } catch (err) {
      const error = {
        code: 'UPLOAD_FAILED' as FileUploadErrorCodeType,
        message: err instanceof Error ? err.message : 'Upload failed',
      };
      setState({ status: 'error', error });
      onError?.(error);
    }
  }, [uploadService, category, allowedMimeTypes, maxFileSize, onSuccess, onError]);

  const reset = useCallback(() => {
    setState({ status: 'idle' });
  }, []);

  // Computed helpers
  const isUploading = state.status === 'uploading';
  const isSuccess = state.status === 'success';
  const isError = state.status === 'error';
  const result = state.status === 'success' ? state.result : null;
  const error = state.status === 'error' ? state.error : null;

  return {
    state,
    upload,
    reset,
    isUploading,
    isSuccess,
    isError,
    result,
    error,
  };
}
